import type { ApiError } from '@erp-attach/shared';
import type {
  AuditPage,
  AuditQuery,
  BrowseCustomer,
  BrowseFacets,
  BrowseQuery,
  BrowseSupplier,
  CompleteUploadResponse,
  CreateFolderGrantRequest,
  CreateUploadRequest,
  CreateUploadResponse,
  CustomerEntity,
  DocumentDetail,
  DocumentTypeAdmin,
  DocumentTypeUpdate,
  EntityResponse,
  FolderDocumentsResponse,
  FolderGrant,
  JobsResponse,
  MeResponse,
  RoleMapping,
  SearchResponse,
  SiteEntity,
  SupplierEntity,
  SyncRunsQuery,
  SyncRunsResponse,
  SyncStatusResponse,
} from './types';

export type TokenGetter = () => Promise<string>;

/** Error raised for any non-2xx response. Mirrors `{ error: { code, message, correlationId } }`. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string;

  constructor(status: number, code: string, message: string, correlationId: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export interface ApiClientOptions {
  getToken: TokenGetter;
  /** Defaults to '' so requests go to the same origin (Vite proxies /api in dev). */
  baseUrl?: string;
  fetchFn?: typeof fetch;
  /** Override for tests; defaults to crypto.randomUUID. */
  newCorrelationId?: () => string;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, string | number | boolean | undefined | null>;
  json?: unknown;
  body?: BodyInit;
  contentType?: string;
  signal?: AbortSignal;
}

export interface BinaryResponse {
  blob: Blob;
  contentType: string;
  filename: string | null;
  correlationId: string | null;
}

function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

async function toError(res: Response, fallbackCorrelationId: string): Promise<ApiRequestError> {
  const correlationId = res.headers.get('x-correlation-id') ?? fallbackCorrelationId;
  let code = `HTTP_${res.status}`;
  let message = res.statusText || `Request failed with status ${res.status}`;
  try {
    const parsed = (await res.json()) as Partial<ApiError>;
    if (parsed?.error) {
      code = parsed.error.code ?? code;
      message = parsed.error.message ?? message;
      return new ApiRequestError(res.status, code, message, parsed.error.correlationId ?? correlationId);
    }
  } catch {
    /* non-JSON error body */
  }
  return new ApiRequestError(res.status, code, message, correlationId);
}

export function createApiClient(opts: ApiClientOptions) {
  const baseUrl = (opts.baseUrl ?? '').replace(/\/+$/, '');
  const fetchFn = opts.fetchFn ?? ((input, init) => fetch(input, init));
  const newCorrelationId = opts.newCorrelationId ?? (() => crypto.randomUUID());

  async function rawRequest(path: string, options: RequestOptions = {}): Promise<Response> {
    const token = await opts.getToken();
    const correlationId = newCorrelationId();
    const headers = new Headers({
      Authorization: `Bearer ${token}`,
      'x-correlation-id': correlationId,
      Accept: 'application/json',
    });
    let body: BodyInit | undefined;
    if (options.json !== undefined) {
      headers.set('Content-Type', 'application/json');
      body = JSON.stringify(options.json);
    } else if (options.body !== undefined) {
      headers.set('Content-Type', options.contentType ?? 'application/octet-stream');
      body = options.body;
    }
    const res = await fetchFn(`${baseUrl}${path}${buildQuery(options.query)}`, {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal,
      credentials: 'same-origin',
    });
    if (!res.ok) throw await toError(res, correlationId);
    return res;
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const res = await rawRequest(path, options);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  async function binary(path: string, signal?: AbortSignal): Promise<BinaryResponse> {
    const res = await rawRequest(path, { signal });
    return {
      blob: await res.blob(),
      contentType: res.headers.get('content-type') ?? 'application/octet-stream',
      filename: filenameFromDisposition(res.headers.get('content-disposition')),
      correlationId: res.headers.get('x-correlation-id'),
    };
  }

  return {
    request,
    me: (signal?: AbortSignal) => request<MeResponse>('/api/me', { signal }),

    customers: {
      get: (accountNumber: string, signal?: AbortSignal) =>
        request<EntityResponse<CustomerEntity>>(`/api/customers/${encodeURIComponent(accountNumber)}`, { signal }),
      jobs: (accountNumber: string, status: 'open' | 'archived' | 'all', signal?: AbortSignal) =>
        request<JobsResponse>(`/api/customers/${encodeURIComponent(accountNumber)}/jobs`, {
          query: { status },
          signal,
        }),
      /** Account + party site number → the site id of /jobs/:siteId (the Collections link, spec 2026-10-20). */
      site: (accountNumber: string, partySiteNumber: string, signal?: AbortSignal) =>
        request<{ siteId: string }>(`/api/customers/${encodeURIComponent(accountNumber)}/sites/${encodeURIComponent(partySiteNumber)}`, { signal }),
    },

    jobs: {
      get: (siteId: string, signal?: AbortSignal) =>
        request<EntityResponse<SiteEntity>>(`/api/jobs/${encodeURIComponent(siteId)}`, { signal }),
      archive: (siteId: string) =>
        request<{ ok: true }>(`/api/jobs/${encodeURIComponent(siteId)}/archive`, { method: 'POST', json: {} }),
      reopen: (siteId: string) =>
        request<{ ok: true }>(`/api/jobs/${encodeURIComponent(siteId)}/reopen`, { method: 'POST', json: {} }),
    },

    suppliers: {
      get: (supplierNumber: string, signal?: AbortSignal) =>
        request<EntityResponse<SupplierEntity>>(`/api/suppliers/${encodeURIComponent(supplierNumber)}`, { signal }),
    },

    folders: {
      documents: (folderId: string, includeHidden = false, signal?: AbortSignal) =>
        request<FolderDocumentsResponse>(`/api/folders/${encodeURIComponent(folderId)}/documents`, {
          query: { includeHidden: includeHidden ? 'true' : undefined },
          signal,
        }),
    },

    uploads: {
      create: (body: CreateUploadRequest, signal?: AbortSignal) =>
        request<CreateUploadResponse>('/api/uploads', { method: 'POST', json: body, signal }),
      putBlock: (uploadId: string, blockNumber: number, block: Blob, signal?: AbortSignal) =>
        rawRequest(`/api/uploads/${encodeURIComponent(uploadId)}/blocks/${blockNumber}`, {
          method: 'PUT',
          body: block,
          contentType: 'application/octet-stream',
          signal,
        }).then(() => undefined),
      complete: (uploadId: string, signal?: AbortSignal) =>
        request<CompleteUploadResponse>(`/api/uploads/${encodeURIComponent(uploadId)}/complete`, {
          method: 'POST',
          json: {},
          signal,
        }),
    },

    documents: {
      get: (id: string, signal?: AbortSignal) =>
        request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}`, { signal }),
      contentUrl: (id: string) => `${baseUrl}/api/documents/${encodeURIComponent(id)}/content`,
      downloadUrl: (id: string) => `${baseUrl}/api/documents/${encodeURIComponent(id)}/download`,
      /** Fetches the bytes with the bearer (the API audits before the first byte). */
      content: (id: string, signal?: AbortSignal) => binary(`/api/documents/${encodeURIComponent(id)}/content`, signal),
      download: (id: string, signal?: AbortSignal) => binary(`/api/documents/${encodeURIComponent(id)}/download`, signal),
      hide: (id: string) => request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}/hide`, { method: 'POST', json: {} }),
      unhide: (id: string) =>
        request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}/unhide`, { method: 'POST', json: {} }),
      remove: (id: string, reason: string) =>
        request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}/remove`, { method: 'POST', json: { reason } }),
      hold: (id: string, hold: boolean, reason: string) =>
        request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}/hold`, {
          method: 'POST',
          json: { hold, reason },
        }),
      move: (id: string, folderId: string) =>
        request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}/move`, { method: 'POST', json: { folderId } }),
      rename: (id: string, title: string) =>
        request<DocumentDetail>(`/api/documents/${encodeURIComponent(id)}/rename`, { method: 'POST', json: { title } }),
    },

    search: (q: string, signal?: AbortSignal) => request<SearchResponse>('/api/search', { query: { q }, signal }),

    browse: {
      customerFacets: (signal?: AbortSignal) => request<BrowseFacets>('/api/browse/customers', { signal }),
      customers: (q: BrowseQuery, signal?: AbortSignal) =>
        request<{ customers: BrowseCustomer[]; nextCursor: string | null }>('/api/browse/customers/items', {
          query: { branch: q.branch, letter: q.letter, cursor: q.cursor, limit: q.limit },
          signal,
        }),
      supplierFacets: (signal?: AbortSignal) => request<BrowseFacets>('/api/browse/suppliers', { signal }),
      suppliers: (q: BrowseQuery, signal?: AbortSignal) =>
        request<{ suppliers: BrowseSupplier[]; nextCursor: string | null }>('/api/browse/suppliers/items', {
          query: { letter: q.letter, cursor: q.cursor, limit: q.limit },
          signal,
        }),
    },

    admin: {
      // The admin routes wrap their payloads ({ documentTypes }, { mappings }, …); unwrap here.
      documentTypes: (signal?: AbortSignal) =>
        request<{ documentTypes: DocumentTypeAdmin[] }>('/api/admin/document-types', { signal }).then((r) => r.documentTypes),
      updateDocumentType: (code: string, patch: DocumentTypeUpdate) =>
        request<{ documentType: DocumentTypeAdmin }>(`/api/admin/document-types/${encodeURIComponent(code)}`, {
          method: 'PUT',
          json: patch,
        }).then((r) => r.documentType),
      roleMappings: (signal?: AbortSignal) =>
        request<{ mappings: RoleMapping[] }>('/api/admin/role-mappings', { signal }).then((r) => r.mappings),
      putRoleMappings: (mappings: RoleMapping[]) =>
        request<{ mappings: RoleMapping[] }>('/api/admin/role-mappings', { method: 'PUT', json: { mappings } }).then((r) => r.mappings),
      folderGrants: (folderId?: string, signal?: AbortSignal) =>
        request<{ grants: FolderGrant[] }>('/api/admin/folder-grants', { query: { folderId }, signal }).then((r) => r.grants),
      createFolderGrant: (body: CreateFolderGrantRequest) =>
        request<{ grant: FolderGrant }>('/api/admin/folder-grants', { method: 'POST', json: body }).then((r) => r.grant),
      /** Oracle sync observability: watermarks, per-source status, entity counts, last 50 runs. */
      sync: (signal?: AbortSignal) => request<SyncStatusResponse>('/api/admin/sync', { signal }),
      syncRuns: (q: SyncRunsQuery, signal?: AbortSignal) =>
        request<SyncRunsResponse>('/api/admin/sync/runs', {
          query: { source: q.source, limit: q.limit, before: q.before },
          signal,
        }),
    },

    audit: (query: AuditQuery, signal?: AbortSignal) =>
      request<AuditPage>('/api/audit', { query: { ...query }, signal }),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
